import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import './AdminTests.css'
import LoadingScreen from '../components/LoadingScreen'
import alert from '../utils/alertService'
import ConfirmModal from '../components/ConfirmModal'

const AdminTests = () => {
  const [tests, setTests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [filter, setFilter] = useState('all')
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [testToDelete, setTestToDelete] = useState(null)
  const history = useHistory()

  useEffect(() => {
    fetchTests()
  }, [])

  const fetchTests = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/admin/tests', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setTests(data.tests || [])
      } else {
        setError('Failed to load tests')
      }
    } catch (err) {
      setError('Network error')
    } finally {
      setLoading(false)
    }
  }

  const confirmDeleteTest = (test) => {
    setTestToDelete(test)
    setDeleteModalOpen(true)
  }

  const handleDeleteTest = async () => {
    if (!testToDelete) return
    const testId = testToDelete.id

    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`/api/admin/tests/${testId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        setTests(tests.filter(test => test.id !== testId))
        alert.success('Test deleted successfully')
      } else {
        alert.error('Failed to delete test')
      }
    } catch (err) {
      alert.error('Network error')
    }
  }

  const getFilteredTests = () => {
    let filtered = tests

    if (filter !== 'all') {
      filtered = filtered.filter(test => test.status === filter)
    }

    if (searchTerm) {
      filtered = filtered.filter(test =>
        test.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        test.teacher_name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    return filtered
  }

  if (loading) {
    return <LoadingScreen message="Loading Tests" />
  }

  const filteredTests = getFilteredTests()

  return (
    <div className="admin-tests-container">
      <header className="page-header">
        <h1>Test Management</h1>
        <button onClick={() => history.push('/admin-dashboard')} className="back-button">
          Back to Dashboard
        </button>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="admin-tests-content">
        <div className="controls-section">
          <input
            type="text"
            placeholder="Search tests or teachers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />

          <div className="filter-buttons">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All ({tests.length})
            </button>
            <button
              className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
              onClick={() => setFilter('active')}
            >
              Active
            </button>
            <button
              className={`filter-btn ${filter === 'draft' ? 'active' : ''}`}
              onClick={() => setFilter('draft')}
            >
              Draft
            </button>
            <button
              className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
              onClick={() => setFilter('completed')}
            >
              Completed
            </button>
          </div>
        </div>

        <div className="tests-table-container">
          {filteredTests.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📝</div>
              <h3>No Tests Found</h3>
              <p>No tests match your search criteria</p>
            </div>
          ) : (
            <table className="tests-table rts-stack-table">
              <thead>
                <tr>
                  <th>Test Title</th>
                  <th>Teacher</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Duration</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTests.map((test) => (
                  <tr key={test.id}>
                    <td data-label="Test Title" className="test-title">{test.title}</td>
                    <td data-label="Teacher">{test.teacher_name}</td>
                    <td data-label="Status">
                      <span className={`status-badge status-${test.status}`}>
                        {test.status}
                      </span>
                    </td>
                    <td data-label="Attempts" className="text-center">{test.total_attempts || 0}</td>
                    <td data-label="Duration" className="text-center">{test.duration} min</td>
                    <td data-label="Created">{new Date(test.created_at).toLocaleDateString()}</td>
                    <td data-label="Actions">
                      <div className="action-buttons">
                        <button className="action-btn view-btn">View</button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => confirmDeleteTest(test)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false)
          setTestToDelete(null)
        }}
        onConfirm={handleDeleteTest}
        title="Delete Test"
        message={`Are you sure you want to delete "${testToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        isDangerous={true}
      />
    </div>
  )
}

export default AdminTests
